import React, { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { authService } from '../services';
import { useToast } from '../hooks';
import { useZodForm } from '../hooks';
import { registerSchema, RegisterFormData } from '../schemas';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle } from '@fortawesome/free-solid-svg-icons';

// Brand logo/icon
const LogoIcon = () => (
  <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="10" className="fill-primary-600" />
    <path
      d="M12 14h6v6h-6v-6zm10 0h6v3h-6v-3zm0 6h6v8h-6v-8zm-10 3h6v5h-6v-5z"
      className="fill-white"
    />
  </svg>
);

// Role icons
const OwnerIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const WorkerIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useZodForm<RegisterFormData>(registerSchema, {
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'worker',
    },
    mode: 'onBlur',
  });

  const password = watch('password');
  const role = watch('role');

  const passwordValidation = useMemo(() => {
    const criteria = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    };
    return { criteria };
  }, [password]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const response = await authService.register({
        email: data.email,
        password: data.password,
        full_name: data.fullName,
        role: data.role,
      });
      setUser(response.user, response.accessToken, response.refreshToken);
      navigate('/dashboard');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      let errorMessage = 'Registration failed. Please try again.';
      if (Array.isArray(detail)) {
        errorMessage = detail.map((e: any) => e.msg).join(', ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      }
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex bg-neutral-50">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 to-primary-800 p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-surface/20 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zm0 6a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1v-7zm-10 2a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5z"
                />
              </svg>
            </div>
            <span className="text-xl font-semibold text-white">Orchestrate</span>
          </div>
        </div>

        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight">
            Join the platform
            <br />
            built for teams
          </h1>
          <p className="text-primary-100 text-lg max-w-md">
            Whether you're managing workflows or executing tasks, Orchestrate helps you work smarter
            together.
          </p>

          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="bg-surface/10 rounded-lg p-4">
              <div className="text-2xl font-bold text-white">Visual</div>
              <div className="text-sm text-primary-200">Workflow Builder</div>
            </div>
            <div className="bg-surface/10 rounded-lg p-4">
              <div className="text-2xl font-bold text-white">Real-time</div>
              <div className="text-sm text-primary-200">Progress Tracking</div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 text-primary-200 text-sm">
          <span>Free to get started</span>
          <span className="w-1 h-1 rounded-full bg-primary-300" />
          <span>No credit card required</span>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="flex items-center gap-3">
              <LogoIcon />
              <span className="text-xl font-semibold text-neutral-900">Orchestrate</span>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900">Create your account</h2>
            <p className="mt-2 text-sm text-neutral-500">Get started with Orchestrate in minutes</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <Input
              {...register('fullName')}
              label="Full name"
              required
              type="text"
              placeholder="John Doe"
              error={!!errors.fullName}
              errorMessage={errors.fullName?.message}
              autoComplete="name"
            />

            <Input
              {...register('email')}
              label="Email"
              required
              type="email"
              placeholder="you@example.com"
              error={!!errors.email}
              errorMessage={errors.email?.message}
              autoComplete="email"
            />

            <div>
              <Input
                {...register('password')}
                label="Password"
                required
                type="password"
                placeholder="Create a password"
                error={!!errors.password}
                autoComplete="new-password"
              />
              <div className="mt-2">
                {Object.entries({
                  '8 characters minimum': passwordValidation.criteria.minLength,
                  '1 upper case letter (A-Z)': passwordValidation.criteria.hasUppercase,
                  '1 number (0-9)': passwordValidation.criteria.hasNumber,
                  '1 special character (!@#$%^&*()-_+=)':
                    passwordValidation.criteria.hasSpecialChar,
                }).map(([rule, valid]) => (
                  <span
                    key={rule}
                    className={`font-medium text-sm block ${valid ? 'text-neutral-700' : 'text-neutral-500'}`}
                  >
                    {valid ? <FontAwesomeIcon icon={faCheck} size="xs" /> : <FontAwesomeIcon icon={faCircle} size="xs" />} {rule}
                  </span>
                ))}
              </div>
            </div>

            <Input
              {...register('confirmPassword')}
              label="Confirm password"
              required
              type="password"
              placeholder="Confirm your password"
              error={!!errors.confirmPassword}
              errorMessage={errors.confirmPassword?.message}
              autoComplete="new-password"
            />

            {/* Role Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">I want to</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setValue('role', 'owner', { shouldDirty: true })}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    role === 'owner'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300 bg-surface'
                  }`}
                >
                  <div
                    className={`mb-2 ${role === 'owner' ? 'text-primary-600' : 'text-neutral-400'}`}
                  >
                    <OwnerIcon />
                  </div>
                  <div
                    className={`font-medium ${role === 'owner' ? 'text-primary-900' : 'text-neutral-900'}`}
                  >
                    Create workflows
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    Build and manage team workflows
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setValue('role', 'worker', { shouldDirty: true })}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    role === 'worker'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300 bg-surface'
                  }`}
                >
                  <div
                    className={`mb-2 ${role === 'worker' ? 'text-primary-600' : 'text-neutral-400'}`}
                  >
                    <WorkerIcon />
                  </div>
                  <div
                    className={`font-medium ${role === 'worker' ? 'text-primary-900' : 'text-neutral-900'}`}
                  >
                    Join a team
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">Execute tasks assigned to you</div>
                </button>
              </div>

              {role === 'worker' && (
                <p className="text-xs text-neutral-500 bg-neutral-100 rounded-lg p-3 mt-2">
                  After registration, you'll be able to browse and request to join available teams.
                </p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting || !isDirty || !isValid}
            >
              Create account
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-neutral-500">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
